import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { GitBranch, UserPlus, UserMinus, Calendar, Clock, AlertCircle } from 'lucide-react';
import { staffAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (t) => {
  if (!t) return '—';
  const parts = t.split(':').map(Number);
  const h = parts[0];
  const m = parts[1] || 0;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const NightworkAmendmentDiff = ({ amendmentId }) => {
  const { data: diff, isLoading, error } = useQuery({
    queryKey: ['nightwork-amendment-diff', amendmentId],
    queryFn: () => staffAPI.getNightworkAmendmentDiff(amendmentId),
    select: (r) => r.data,
    enabled: !!amendmentId,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-6 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    // 400 = "not an amendment request" (plain draft / pending edit) — expected
    // when this is rendered defensively; fail silently rather than showing an error card.
    if (error.response?.status === 400) return null;
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Diff</h3>
        <p className="text-sm text-gray-500">Could not load the amendment comparison.</p>
      </div>
    );
  }

  if (!diff) return null;

  const { changes, date_changes, time_changes, worker_date_changes } = diff;
  const hasWorkerChanges = changes.workers_added.length > 0 || changes.workers_removed.length > 0;
  const hasSafetyChanges = changes.safety_added.length > 0 || changes.safety_removed.length > 0;
  const hasDateChanges = Object.keys(date_changes || {}).length > 0;
  const hasTimeChanges = Object.keys(time_changes || {}).length > 0;
  const hasWorkerDateChanges = (worker_date_changes || []).length > 0;
  const hasAnyChange = hasWorkerChanges || hasSafetyChanges || hasDateChanges || hasTimeChanges || hasWorkerDateChanges;

  if (!hasAnyChange) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-purple-600" />
            Amendment Changes
          </h3>
        </div>
        <div className="card-content">
          <p className="text-sm text-gray-500 text-center py-4">No changes detected yet. Edit the amendment to make changes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-purple-600" />
            Amendment Changes
          </h3>
          <span className="text-xs text-gray-500">
            {diff.original_request_number} → v{diff.amendment_version}
          </span>
        </div>
      </div>
      <div className="card-content space-y-4">
        {/* Personnel Changes */}
        {hasWorkerChanges && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Workers</p>
            <div className="space-y-1.5">
              {changes.workers_added.map((w) => (
                <div key={w.staff_id} className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                  <UserPlus className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-800 flex-1">{w.name}</span>
                  <span className="text-xs text-green-600 font-mono">{w.staff_id}</span>
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">Added</span>
                </div>
              ))}
              {changes.workers_removed.map((w) => (
                <div key={w.staff_id} className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                  <UserMinus className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-800 flex-1 line-through">{w.name}</span>
                  <span className="text-xs text-red-600 font-mono">{w.staff_id}</span>
                  <span className="text-xs font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">Removed</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {hasSafetyChanges && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Safety Personnel</p>
            <div className="space-y-1.5">
              {changes.safety_added.map((w) => (
                <div key={w.staff_id} className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                  <UserPlus className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-800 flex-1">{w.name}</span>
                  <span className="text-xs text-green-600 font-mono">{w.staff_id}</span>
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">Added</span>
                </div>
              ))}
              {changes.safety_removed.map((w) => (
                <div key={w.staff_id} className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                  <UserMinus className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-800 flex-1 line-through">{w.name}</span>
                  <span className="text-xs text-red-600 font-mono">{w.staff_id}</span>
                  <span className="text-xs font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">Removed</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-Worker Date Changes */}
        {hasWorkerDateChanges && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Worker Date Assignments</p>
            <div className="space-y-1.5">
              {worker_date_changes.map((w) => (
                <div key={w.staff_id} className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg">
                  <Calendar className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                  <span className="text-sm text-gray-900 flex-1">{w.name}</span>
                  <span className="text-xs text-red-600 line-through">
                    {w.original_from ? formatDate(w.original_from) : 'Full'} – {w.original_until ? formatDate(w.original_until) : 'Full'}
                  </span>
                  <span className="text-xs text-gray-400">→</span>
                  <span className="text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                    {w.amendment_from ? formatDate(w.amendment_from) : 'Full'} – {w.amendment_until ? formatDate(w.amendment_until) : 'Full'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date Changes */}
        {hasDateChanges && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Schedule Dates</p>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              {date_changes.original_start && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-500">Date Range:</span>
                  <span className="text-red-600 line-through">
                    {formatDate(date_changes.original_start)} – {formatDate(date_changes.original_end)}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className="text-green-700 font-medium">
                    {formatDate(date_changes.amendment_start)} – {formatDate(date_changes.amendment_end)}
                  </span>
                </div>
              )}
              {date_changes.original_specific && (
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-500">Specific Dates Changed</span>
                  </div>
                  <div className="ml-6 text-xs text-gray-600">
                    Before: {(date_changes.original_specific || []).map(formatDate).join(', ')}
                  </div>
                  <div className="ml-6 text-xs text-green-700 font-medium">
                    After: {(date_changes.amendment_specific || []).map(formatDate).join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Time Changes */}
        {hasTimeChanges && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Shift Time</p>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-3 text-sm">
                <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-red-600 line-through">
                  {formatTime(time_changes.original_start_time)} – {formatTime(time_changes.original_end_time)}
                </span>
                <span className="text-gray-400">→</span>
                <span className="text-green-700 font-medium">
                  {formatTime(time_changes.amendment_start_time)} – {formatTime(time_changes.amendment_end_time)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NightworkAmendmentDiff;
